import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { MFASetup } from './MFASetup';
import { MFAVerification } from './MFAVerification';
import { friendlyPreSignupError } from './pre-signup-error-codes';
import { useAuth } from './use-auth';

export function LoginPage() {
    const { user, loading, error, login, signInWithGoogle } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showMFASetup, setShowMFASetup] = useState(false);
    const [showMFAVerification, setShowMFAVerification] = useState(false);
    const [loginError, setLoginError] = useState<string | null>(null);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [isGoogleRedirecting, setIsGoogleRedirecting] = useState(false);

    const params = new URLSearchParams(window.location.search);
    const redirectTo = params.get('redirect') ?? '/';

    if (user) {
        return <Navigate to={redirectTo} replace />;
    }

    const handleGoogleSignIn = () => {
        setLoginError(null);
        setIsGoogleRedirecting(true);

        void (async () => {
            try {
                await signInWithGoogle();
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Google sign-in failed';
                setLoginError(message);
                setIsGoogleRedirecting(false);
            }
        })();
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setLoginError(null);
        setIsLoggingIn(true);

        void (async () => {
            try {
                await login(email, password);
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Login failed';

                if (message === 'MFA_SETUP_REQUIRED') {
                    setShowMFASetup(true);
                } else if (message === 'MFA_CODE_REQUIRED') {
                    setShowMFAVerification(true);
                } else {
                    setLoginError(message);
                }
            } finally {
                setIsLoggingIn(false);
            }
        })();
    };

    if (showMFASetup) {
        return (
            <MFASetup
                onCancel={() => {
                    setShowMFASetup(false);
                }}
            />
        );
    }

    if (showMFAVerification) {
        return (
            <MFAVerification
                onCancel={() => {
                    setShowMFAVerification(false);
                }}
            />
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="w-full max-w-md space-y-8 rounded-lg border bg-card p-8 shadow-lg">
                <div className="text-center">
                    <h2 className="text-3xl font-bold tracking-tight">Slate Admin Portal</h2>
                    <p className="mt-2 text-sm text-muted-foreground">Sign in to your account</p>
                </div>

                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                    {(error ?? loginError) && (
                        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                            {friendlyPreSignupError(error ?? loginError)}
                        </div>
                    )}

                    <div className="space-y-4">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium">
                                Email
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                }}
                                className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                placeholder="admin@example.com"
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium">
                                Password
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                }}
                                className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                placeholder="••••••••"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading || isLoggingIn || isGoogleRedirecting}
                        className="w-full rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {isLoggingIn ? 'Signing in...' : 'Sign in'}
                    </button>
                </form>

                <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={loading || isLoggingIn || isGoogleRedirecting}
                    className="flex w-full items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <GoogleIcon className="h-4 w-4" />
                    {isGoogleRedirecting ? 'Redirecting...' : 'Sign in with Google'}
                </button>
            </div>
        </div>
    );
}

function GoogleIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
            <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.56c2.08-1.92 3.28-4.74 3.28-8.1Z"
                fill="#4285F4"
            />
            <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.56-2.77c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
                fill="#34A853"
            />
            <path
                d="M5.84 14.11A6.6 6.6 0 0 1 5.5 12c0-.74.13-1.45.34-2.11V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z"
                fill="#FBBC05"
            />
            <path
                d="M12 5.38c1.62 0 3.06.56 4.2 1.65l3.15-3.15C17.45 2.1 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"
                fill="#EA4335"
            />
        </svg>
    );
}
